import { useState, useEffect } from 'react';
import { useColumnSelectorStorage } from './columnSelectorStorage';

/**
 * Hook to manage column visibility and order for a table
 * 
 * @param {string} tableId - Unique identifier for the table
 * @param {Array} columns - Array of column objects with {id, name, default} properties
 * @returns {Object} Column selector state and handlers
 */
export const useColumnSelector = (tableId, columns) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const { getVisibleColumns, saveVisibleColumns } = useColumnSelectorStorage();

  useEffect(() => {
    // Get stored columns or use defaults
    const storedColumns = getVisibleColumns(tableId);
    
    if (storedColumns && storedColumns.length > 0) {
      setVisibleColumns(storedColumns);
    } else {
      // If no stored columns, use columns marked as default in their original order
      const defaultColumns = columns
        .filter(column => column.default)
        .map(column => column.id);
      setVisibleColumns(defaultColumns);
    }
  }, [tableId, columns, getVisibleColumns]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleSaveColumns = (selectedColumns) => {
    setVisibleColumns(selectedColumns);
    saveVisibleColumns(tableId, selectedColumns);
  };

  const isColumnVisible = (columnId) => {
    return visibleColumns.includes(columnId);
  };
  
  /**
   * Get the index of a column in the ordered visible columns array
   * @param {string} columnId - The column ID to find the order for
   * @returns {number} The index of the column or -1 if not visible
   */
  const getColumnOrder = (columnId) => {
    return visibleColumns.indexOf(columnId);
  };

  /**
   * Get visible columns with their order information
   * @returns {Array} An array of objects with {id, order} properties
   */
  const getOrderedVisibleColumns = () => {
    return visibleColumns.map((columnId, index) => ({
      id: columnId,
      order: index
    }));
  };

  return {
    modalOpen,
    toggleModal,
    visibleColumns,
    handleSaveColumns,
    isColumnVisible,
    getColumnOrder,
    getOrderedVisibleColumns
  };
};